import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { AlertIcon } from '../shared/Icons';
import { FormProblem } from './formProblems';
import { ADMIN_COLORS } from '../../theme';

interface ProblemsDialogProps {
  open: boolean;
  problems: FormProblem[];
  onClose: () => void;
}

const COLORS = {
  error: { fg: ADMIN_COLORS.danger, bg: ADMIN_COLORS.dangerSoft, border: ADMIN_COLORS.dangerBorder },
  warning: { fg: ADMIN_COLORS.warning, bg: ADMIN_COLORS.warningSoft, border: ADMIN_COLORS.warningBorder },
};

export const ProblemsDialog: React.FC<ProblemsDialogProps> = ({ open, problems, onClose }) => {
  const errors = problems.filter((p) => p.severity === 'error');
  const warnings = problems.filter((p) => p.severity === 'warning');

  const renderGroup = (title: string, items: FormProblem[], severity: 'error' | 'warning') =>
    items.length > 0 && (
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: COLORS[severity].fg, mb: 1 }}>
          {title.toUpperCase()} ({items.length})
        </Typography>
        {items.map((problem) => (
          <Box
            key={problem.id}
            sx={{
              display: 'flex',
              gap: 1.25,
              p: 1.5,
              mb: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: COLORS[severity].border,
              bgcolor: COLORS[severity].bg,
            }}
          >
            <Box sx={{ color: COLORS[severity].fg, display: 'flex', pt: 0.25 }}>
              <AlertIcon size={16} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: ADMIN_COLORS.text }}>
                {problem.message}
              </Typography>
              {problem.detail && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
                  {problem.detail}
                </Typography>
              )}
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
                {problem.area}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
        Problems
        {problems.length > 0 && (
          <Typography component="span" sx={{ ml: 1, fontSize: 13, color: 'text.secondary', fontWeight: 400 }}>
            {errors.length} error{errors.length === 1 ? '' : 's'}, {warnings.length} warning
            {warnings.length === 1 ? '' : 's'}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {problems.length === 0 ? (
          <Typography sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            No problems found.
          </Typography>
        ) : (
          <>
            {renderGroup('Errors', errors, 'error')}
            {renderGroup('Warnings', warnings, 'warning')}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProblemsDialog;
